import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEOHead from '../SEO/SEOHead';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const stagger = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const calgaryVerticals = [
  {
    industry: 'Energy & Oil and Gas',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    description: 'Calgary is the energy capital of Canada. We help oil and gas companies modernize legacy systems, build AI-driven predictive maintenance pipelines, optimize cloud infrastructure costs, and implement real-time data analytics for upstream and midstream operations.',
    services: ['Predictive maintenance with AI agents', 'SCADA data pipeline modernization', 'Multi-cloud cost optimization for HPC workloads', 'Real-time production analytics dashboards'],
  },
  {
    industry: 'Agriculture & AgTech',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    description: 'Alberta is at the forefront of precision agriculture and agtech innovation. We build intelligent systems that leverage satellite imagery, IoT sensor data, and machine learning to optimize yield predictions, supply chain logistics, and resource allocation.',
    services: ['IoT data pipeline architecture', 'Machine learning yield prediction models', 'Supply chain optimization platforms', 'Precision agriculture data dashboards'],
  },
  {
    industry: 'Financial Services & Fintech',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    description: 'Calgary\'s growing fintech and financial services sector needs robust, compliant technology infrastructure. We deliver cloud-native platforms, AI-powered risk assessment systems, and secure data architectures that meet Canadian regulatory requirements.',
    services: ['SOC 2 compliant cloud architecture', 'AI-driven risk and fraud detection', 'Real-time transaction processing systems', 'Regulatory data reporting automation'],
  },
  {
    industry: 'Construction & Real Estate Tech',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    description: 'Calgary\'s construction and real estate industry is ripe for digital transformation. We help companies implement project management platforms, AI-powered estimating tools, BIM data integration, and cloud-based collaboration systems that reduce project timelines and costs.',
    services: ['Cloud-based project management platforms', 'AI-assisted cost estimation systems', 'BIM data integration and analytics', 'IoT-enabled site monitoring dashboards'],
  },
];

const services = [
  {
    title: 'Fractional CTO Services',
    description: 'Executive-level technology leadership for Calgary businesses. Architecture reviews, engineering roadmaps, technical due diligence, and strategic guidance—without the full-time overhead.',
    link: '/services',
  },
  {
    title: 'AI & Agentic Implementation',
    description: 'Production-grade AI systems for Alberta enterprises. LLM integration, RAG pipelines, autonomous agents, and intelligent automation tailored to your industry.',
    link: '/services',
  },
  {
    title: 'Cloud Architecture & Migration',
    description: 'Multi-cloud strategy across AWS, GCP, and Azure. Infrastructure as code, Kubernetes orchestration, and cost optimization for Calgary-based organizations.',
    link: '/services',
  },
  {
    title: 'Data & Analytics',
    description: 'Modern data stack implementation—BigQuery, Snowflake, dbt, Looker. Turn operational data into strategic insight with pipelines built for Alberta enterprise scale.',
    link: '/services',
  },
];

const CalgaryPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="Technology Consulting Calgary | Fractional CTO & AI Strategy"
        description="North provides fractional CTO services, AI strategy consulting, cloud architecture, and data analytics for Calgary and Alberta businesses. Serving energy, agriculture, fintech, and construction sectors."
        path="/calgary"
      />

      {/* Hero Section with Top CTA */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-cream via-white to-pink-50 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-100/40 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-orange-50/40 to-transparent rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 relative">
          <motion.div {...fadeIn} className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full mb-6 border border-gray-100">
              <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              <span className="text-sm font-medium text-dark-slate">Serving Calgary & Alberta</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-dark-slate leading-[1.05] mb-6">
              Technology Consulting{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Calgary</span>
            </h1>

            <p className="text-xl text-muted-grey leading-relaxed mb-8 max-w-3xl">
              Fractional CTO services, AI strategy, cloud architecture, and data analytics for Calgary and Alberta businesses.
              We help energy companies, agtech startups, fintech firms, and construction enterprises build and scale modern technology platforms.
            </p>

            {/* Top CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.a
                href="https://calendar.app.google/ube4SfQSbn6SqRCCA"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-dark-slate text-white rounded-full font-semibold text-lg hover:bg-deep-charcoal transition-colors inline-flex items-center justify-center gap-2"
              >
                Book a Strategy Call
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </motion.a>
              <Link
                to="/services"
                className="px-8 py-4 border-2 border-dark-slate text-dark-slate rounded-full font-semibold text-lg hover:bg-dark-slate hover:text-white transition-colors inline-flex items-center justify-center"
              >
                View Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeIn} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-dark-slate mb-4">
              What We Deliver in Calgary
            </h2>
            <p className="text-lg text-muted-grey max-w-2xl mx-auto">
              Enterprise-grade technology consulting tailored to Calgary's unique business landscape and industry needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((svc, i) => (
              <motion.div
                key={svc.title}
                {...stagger}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-8 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all"
              >
                <h3 className="text-xl font-bold text-dark-slate mb-3">{svc.title}</h3>
                <p className="text-muted-grey leading-relaxed mb-4">{svc.description}</p>
                <Link to={svc.link} className="text-dark-slate font-medium hover:text-deep-charcoal transition-colors inline-flex items-center gap-1">
                  Learn more
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Verticals */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeIn} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-dark-slate mb-4">
              Calgary Industry Expertise
            </h2>
            <p className="text-lg text-muted-grey max-w-3xl mx-auto">
              Deep understanding of the industries that drive Alberta's economy. We build technology solutions that address the specific challenges and opportunities in each sector.
            </p>
          </motion.div>

          <div className="space-y-12">
            {calgaryVerticals.map((vertical, i) => (
              <motion.div
                key={vertical.industry}
                {...stagger}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`flex flex-col lg:flex-row gap-8 items-start p-8 rounded-2xl ${
                  i % 2 === 0 ? 'bg-white border border-gray-100' : 'bg-cream/50'
                }`}
              >
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center text-blue-600">
                  {vertical.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-dark-slate mb-3">{vertical.industry}</h3>
                  <p className="text-muted-grey leading-relaxed mb-4">{vertical.description}</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {vertical.services.map((svc) => (
                      <div key={svc} className="flex items-start gap-2 text-sm text-dark-slate">
                        <svg className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {svc}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Calgary Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeIn}>
              <h2 className="text-3xl md:text-4xl font-bold text-dark-slate mb-6">
                Why Calgary Businesses Choose North
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold">1</div>
                  <div>
                    <h4 className="font-bold text-dark-slate mb-1">Local Understanding, Global Expertise</h4>
                    <p className="text-muted-grey">We understand Calgary's business culture and the unique needs of Alberta industries, backed by experience delivering enterprise technology across North America.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold">2</div>
                  <div>
                    <h4 className="font-bold text-dark-slate mb-1">Energy Sector Depth</h4>
                    <p className="text-muted-grey">Deep experience with the specific data, infrastructure, and compliance challenges that oil and gas, pipeline, and energy services companies face.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold">3</div>
                  <div>
                    <h4 className="font-bold text-dark-slate mb-1">Startup & Scale-Up Focus</h4>
                    <p className="text-muted-grey">Calgary's tech ecosystem is booming. We help startups build the right foundation from day one and scale-ups avoid the architectural mistakes that slow growth.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold">4</div>
                  <div>
                    <h4 className="font-bold text-dark-slate mb-1">Canadian Data Residency</h4>
                    <p className="text-muted-grey">We architect solutions that keep your data in Canada when regulatory or business requirements demand it, with multi-region cloud strategies across GCP, AWS, and Azure Canada regions.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl p-10">
              <div className="space-y-8">
                <div>
                  <div className="text-4xl font-bold text-dark-slate">15+</div>
                  <div className="text-muted-grey mt-1">Years Engineering Leadership</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-dark-slate">50+</div>
                  <div className="text-muted-grey mt-1">Enterprise Engagements</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-dark-slate">3</div>
                  <div className="text-muted-grey mt-1">Major Cloud Platforms (AWS, GCP, Azure)</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-dark-slate">30-50%</div>
                  <div className="text-muted-grey mt-1">Avg Cloud Cost Reduction</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Also Serving Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-muted-grey mb-4">Also serving businesses across Western Canada</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/vancouver" className="px-6 py-2 bg-white rounded-full text-dark-slate font-medium border border-gray-200 hover:border-dark-slate transition-colors">
              Vancouver
            </Link>
            <span className="px-6 py-2 bg-white rounded-full text-dark-slate font-medium border border-gray-200">
              Edmonton
            </span>
            <span className="px-6 py-2 bg-white rounded-full text-dark-slate font-medium border border-gray-200">
              Toronto
            </span>
            <span className="px-6 py-2 bg-white rounded-full text-dark-slate font-medium border border-gray-200">
              All of Canada
            </span>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-20 bg-dark-slate text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeIn}>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Calgary Business?
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
              Whether you need a fractional CTO, AI strategy, cloud migration, or data analytics—book a free strategy call and let's discuss how we can accelerate your technology roadmap.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://calendar.app.google/ube4SfQSbn6SqRCCA"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white text-dark-slate rounded-full font-semibold text-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2"
              >
                Book a Strategy Call
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </motion.a>
              <a
                href="mailto:info@northinteractive.co"
                className="px-8 py-4 border-2 border-white text-white rounded-full font-semibold text-lg hover:bg-white hover:text-dark-slate transition-colors inline-flex items-center justify-center"
              >
                Email Us Directly
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default CalgaryPage;
